import { ImportOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, ConfigProvider, Modal, Select, Space, Table, Tag, Typography, message, theme } from 'antd';
import { useRef, useState } from 'react';
import * as xlsx from 'xlsx';
import apis from '../../../apis';

const attendanceLabel = value => value === true ? 'Attending' : value === false ? 'Not attending' : 'Not imported';

export default function AttendanceImportButton({ onSaved }) {
    const fileInput = useRef(null);
    const [sheetRows, setSheetRows] = useState(null);
    const [preview, setPreview] = useState(null);
    const [resolutions, setResolutions] = useState({});
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [filename, setFilename] = useState('');

    const changeResolution = (id, change) => {
        setResolutions(previous => ({ ...previous, [id]: { ...previous[id], ...change } }));
        setDirty(true);
        setResult(null);
    };
    const readFile = async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!/\.(csv|xlsx|xls)$/i.test(file.name) || file.size > 5 * 1024 * 1024) {
            message.error('Choose a CSV or Excel file no larger than 5 MB.');
            return;
        }
        setBusy(true);
        try {
            const workbook = xlsx.read(await file.arrayBuffer(), { type: 'array', raw: false });
            const rows = xlsx.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1, defval: '', raw: false });
            if (new Blob([JSON.stringify({ sheetRows: rows })]).size > 900000) throw new Error('Worksheet data is too large. Split the file into smaller imports.');
            const nextPreview = await apis.registrantAttendance.preview({ sheetRows: rows });
            setSheetRows(rows);
            setPreview(nextPreview);
            setResolutions({});
            setDirty(false);
            setResult(null);
            setFilename(file.name);
        } catch (error) {
            message.error(error.message || 'Unable to read attendance file.');
        } finally { setBusy(false); }
    };
    const refreshPreview = async () => {
        setBusy(true);
        try {
            setPreview(await apis.registrantAttendance.preview({ sheetRows, resolutions }));
            setDirty(false);
            setResult(null);
        } catch (error) { message.error(error.message); }
        finally { setBusy(false); }
    };
    const save = async () => {
        setBusy(true);
        try {
            const matchedIds = new Set(preview.rows.filter(row => row.status === 'ready').flatMap(row => row.matches.map(match => match.id)));
            const snapshots = Object.fromEntries(preview.candidates.filter(candidate => matchedIds.has(candidate.id))
                .map(candidate => [candidate.id, candidate.snapshotToken]));
            const payload = { sheetRows, resolutions, snapshots };
            if (new Blob([JSON.stringify(payload)]).size > 900000) throw new Error('Import data is too large. Split the file into smaller imports.');
            const nextResult = await apis.registrantAttendance.apply(payload);
            setResult(nextResult);
            setDirty(true); // Saving again requires a fresh preview, including after partial success.
            await onSaved?.();
        } catch (error) {
            setDirty(true); // A lost response may follow committed writes; recheck before retrying.
            message.error(`${error.message} Recheck matches before retrying.`);
        } finally { setBusy(false); }
    };
    const close = () => {
        if (busy) return;
        setPreview(null);
        setSheetRows(null);
        setResolutions({});
        setResult(null);
    };
    const candidateOptions = (preview?.candidates || []).map(candidate => ({ value: candidate.id,
        label: `${candidate.names.join(' & ') || 'Unnamed performer'} · ${candidate.eventId} · ${candidate.id}` }));
    const columns = [
        { title: 'Source name / rows', width: 210, render: (_, row) => <><strong>{row.fullName}</strong><div>Rows {row.rowNumbers.join(', ')}</div></> },
        { title: 'Source response', width: 220, render: (_, row) => [...new Set(row.responses)].map((response, index) => <div key={index} style={{ whiteSpace: 'pre-wrap' }}>{response || '(blank)'}</div>) },
        { title: 'Proposed attendance', width: 160, render: (_, row) => row.duplicateConflict
            ? <Select aria-label={`Resolve attendance for ${row.fullName}`} style={{ width: '100%' }} disabled={busy}
                placeholder="Resolve conflict" value={typeof resolutions[row.id]?.willAttend === 'boolean' ? String(resolutions[row.id].willAttend) : undefined}
                options={[{ value: 'true', label: 'Attending' }, { value: 'false', label: 'Not attending' }]}
                onChange={value => changeResolution(row.id, { willAttend: value === 'true' })} />
            : <Tag color={row.willAttend ? 'green' : 'default'}>{attendanceLabel(row.willAttend)}</Tag> },
        { title: 'Matched registrations / current value', width: 360, render: (_, row) => <>
            <Select mode="multiple" maxTagCount="responsive" showSearch optionFilterProp="label" style={{ width: '100%' }} disabled={busy}
                aria-label={`Match registrations for ${row.fullName}`} placeholder="Search performers, event or registration ID"
                value={resolutions[row.id]?.registrantIds ?? row.matches.map(match => match.id)} options={candidateOptions}
                onChange={registrantIds => changeResolution(row.id, { registrantIds })} />
            {row.matches.map(match => <div key={match.id}>{match.eventId} · {match.id}: {attendanceLabel(match.willAttend)}</div>)}
            <Tag color={row.status === 'conflicted' ? 'red' : row.status === 'ready' ? 'green' : 'default'}>{row.status} · {row.matchType}</Tag>
        </> },
        { title: 'Exclude', width: 80, render: (_, row) => <Checkbox disabled={busy} aria-label={`Exclude ${row.fullName}`}
            checked={resolutions[row.id]?.excluded ?? row.excluded} onChange={event => changeResolution(row.id, { excluded: event.target.checked })} /> },
    ];

    return <ConfigProvider theme={{ algorithm: theme.darkAlgorithm,
        token: { colorPrimary: '#EBBC64', colorBgBase: '#121212', colorBgContainer: '#1E1E1E', colorText: '#e5cc92' } }}>
        <input type="file" ref={fileInput} accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={readFile} />
        <Button icon={<ImportOutlined />} onClick={() => fileInput.current?.click()} loading={busy} style={{ marginLeft: 8 }}>Import Attendance</Button>
        <Modal title={`Import Attendance — ${filename}`} open={!!preview} onCancel={close} width={1200}
            maskClosable={!busy} closable={!busy} styles={{ content: { background: '#1E1E1E', color: '#e5cc92' }, header: { background: '#1E1E1E' } }}
            footer={<Space>
                <Button onClick={close} disabled={busy}>Close</Button>
                <Button onClick={refreshPreview} loading={busy}>Recheck matches</Button>
                <Button type="primary" onClick={save} loading={busy}
                    disabled={busy || dirty || !preview?.summary.registrations || !!preview?.summary.conflicted}
                    style={{ background: '#EBBC64', color: '#121212' }}>Save Attendance</Button>
            </Space>}>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Alert type="info" showIcon message="This import updates APCS2025 and APCS2026 registration attendance only."
                    description="Blank and unclear responses count as not attending. Unmatched rows are skipped. Individual names do not automatically match ensembles. Records missing an event ID are treated as APCS2025." />
                {preview && <Typography.Text style={{ color: '#e5cc92' }}>{preview.summary.sourceRows} source rows · {preview.summary.uniqueRows} unique names · {preview.summary.registrations} registrations · {preview.summary.attending} attending · {preview.summary.notAttending} not attending · {preview.summary.skipped} skipped rows · {preview.summary.conflicted} conflicting rows</Typography.Text>}
                {dirty && !result && <Alert type="warning" message="Recheck matches to update the preview before saving." />}
                {!!preview?.summary.conflicted && <Alert type="warning" message="Resolve or exclude conflicting rows, then recheck matches." />}
                {result && <>
                    <Alert showIcon type={result.summary.failed || result.summary.conflictedRegistrations || result.summary.conflicted ? 'warning' : 'success'}
                        message={`${result.summary.updated} updated · ${result.summary.unchanged} unchanged · ${result.summary.skipped} skipped rows · ${result.summary.conflicted} conflicting rows · ${result.summary.conflictedRegistrations} stale registrations · ${result.summary.failed} failed`}
                        description="Completed updates are saved. Recheck matches before another save." />
                    {result.results.some(item => ['failed', 'conflicted'].includes(item.status)) && <Table size="small" rowKey="id" pagination={{ pageSize: 5 }}
                        dataSource={result.results.filter(item => ['failed', 'conflicted'].includes(item.status))}
                        columns={[{ title: 'Registration ID', dataIndex: 'id' }, { title: 'Status', dataIndex: 'status' }, { title: 'Reason', dataIndex: 'message' }]} />}
                </>}
                <Table rowKey="id" size="small" dataSource={preview?.rows || []} columns={columns} scroll={{ x: 1130 }} pagination={{ pageSize: 10, showSizeChanger: true }} />
            </Space>
        </Modal>
    </ConfigProvider>;
}
